import classNames from 'classnames'
import differenceInMilliseconds from 'date-fns/differenceInMilliseconds'
import parseISO from 'date-fns/parseISO'
import * as React from 'react'

import RunningBuildUpdater from '../../../../containers/RunningBuildUpdater'
import useInterval from '../../../../hooks/useInterval'
import {restApi} from '../../../../services/rest'
import stableSort from '../../../../utils/stableSort'
import {useTimelineArg} from '../BuildLog.rest'
import type {TimelineStage as TimelineStageType} from '../BuildLog.types'

import type {Props} from './BuildLogTimeline.types'
import {getLastKnownStatus} from './BuildLogTimeline.utils'
import TimelineStage from './TimelineStage/TimelineStage'
import stageStyles from './TimelineStage/TimelineStage.css'

import styles from './BuildLogTimeline.css'

const ONE_HUNDRED_PERCENT = 100
const INTERVAL = 6000

function BuildLogTimeline({
  startDate,
  buildId,
  className,
  showedFirstDate,
  running,
  logTarget,
  queued,
}: Props) {
  const arg = useTimelineArg(buildId)
  const {ready, stages, nowPoint, refetch} = restApi.endpoints.getBuildLogTimeline.useQuery(arg, {
    selectFromResult: ({data}) => ({...data, ready: data != null}),
  })
  useInterval(refetch, running ? INTERVAL : null)

  if (!ready) {
    return (
      <div className={classNames(styles.timeline, className)}>
        <div className={styles.loading}>
          {queued ? 'In queue' : 'Loading timeline...'}
          <span className={stageStyles.separator} />
          {!queued && <span className={classNames(stageStyles.stageLine, styles.loadingLine)} />}
        </div>
      </div>
    )
  }

  const viewPointMillisecond: number | null | undefined =
    showedFirstDate != null && startDate != null
      ? differenceInMilliseconds(parseISO(showedFirstDate), parseISO(startDate))
      : null
  let stepNumber = 0
  let stageStartMillisecond = 0
  return stages != null && stages.length > 0 ? (
    <div className={classNames(styles.timeline, className)} data-test-build-log-timeline>
      {running && <RunningBuildUpdater buildId={buildId} />}
      {stableSort(
        stages,
        (stage1: TimelineStageType, stage2: TimelineStageType) => stage1.start - stage2.start,
      ).map((stage: TimelineStageType, index: number) => {
        if (running && nowPoint != null && nowPoint < stage.start) {
          return null
        }

        const isStep = !stage.key.startsWith('buildStageDuration:')

        if (isStep) {
          stepNumber++
        }

        let viewPointPercent: number | null | undefined = null

        if (viewPointMillisecond != null) {
          if (
            viewPointMillisecond > stageStartMillisecond &&
            viewPointMillisecond <= stageStartMillisecond + stage.duration
          ) {
            viewPointPercent =
              ((viewPointMillisecond - stageStartMillisecond) * ONE_HUNDRED_PERCENT) /
              stage.duration
          } else if (viewPointMillisecond <= 0 && index === 0) {
            viewPointPercent = 0
          }
        }

        stageStartMillisecond += stage.duration
        return (
          <TimelineStage
            key={stage.key}
            stageKey={stage.key}
            name={stage.name}
            start={stage.start}
            end={stage.end}
            duration={stage.duration}
            running={running && stage.now != null}
            stepNumber={isStep ? stepNumber : null}
            nowX={stage.now?.x}
            buildId={buildId}
            viewPointPercent={viewPointPercent}
            pointName={stage.point?.name}
            pointBuildAnchor={stage.point?.buildAnchor}
            pointType={stage.point?.type}
            pointX={stage.point?.x}
            lastKnownStatus={getLastKnownStatus(stage.statuses)}
            fisrtStatus={stage.statuses != null ? stage.statuses[0] : null}
            secondStatus={stage.statuses != null ? stage.statuses[1] : null}
            isFinish={!running && index === stages.length - 1}
            logTarget={logTarget}
          />
        )
      })}
    </div>
  ) : null
}

export default BuildLogTimeline
